import { normalizationGain } from '@/core/audio/loudness';
import { detectFillers, FILLER_PRESETS, type FillerDetection } from '@/core/audio/fillers';
import { detectSilences, measureLevels, SILENCE_PRESETS, type SilenceDetection, type SilencePresetId } from '@/core/audio/silence';
import { spokenCues, type CaptionCue } from '@/core/captions/types';
import { layoutWithCamera, type CameraSettings } from '@/core/camera/layer';
import type { ScreenActivity } from '@/core/screen/activity';
import { activeZoomSegments, planZoom, type ZoomExclusion, type ZoomSegment, type ZoomSettings } from '@/core/screen/zoom';
import { resolveAspect, type FrameLayout, type Size } from '@/core/geometry/framing';
import { computeFocusPath, type FocusPoint } from '@/core/geometry/reframe';
import { layoutOf } from '@/core/project/arrange';
import { hasSound, resolveLayers, type ResolvedLayer } from '@/core/project/layers';
import { measureTextAspect } from '@/engine/render/text-renderer';
import { placeSound } from '@/core/project/place-media';
import { isTrackHidden, type AudioSettings, type EditRecipe, type FormatSettings, type ManualTrack, type PrivacySettings } from '@/core/project/recipe';
import { assetTimeAt, placeRanges, placeTracks, PRIMARY_ASSET, type AssetId, type SequenceLayout } from '@/core/project/sequence';
import { computeKeepRanges, type Removals } from '@/core/project/timeline';
import type { Telemetry } from '@/core/telemetry/telemetry';
import type { TelemetrySettings } from '@/core/telemetry/overlay';
import { privateRanges, type PrivatePlace } from '@/core/telemetry/zones';
import type { Track } from '@/core/tracking/track';
import { intersectRanges, invertRanges, outputLength, range, toOutputRanges, type PlayRange, type TimeRange } from '@/core/time/ranges';
import type { AudioAnalysis, ExportClip, ExportJob, MediaInfo } from '@/engine/types';

/**
 * Pure derivations from editor state. Each selector takes only the slice it
 * needs, which keeps memoization precise and the functions easy to test.
 */
const NO_SILENCE: SilenceDetection = { silences: [], levels: null };

export function selectSilences(analysis: AudioAnalysis | null | undefined, preset: SilencePresetId, durationSec: number): SilenceDetection {
  if (!analysis) return NO_SILENCE;
  return detectSilences(analysis.envelope, SILENCE_PRESETS[preset], durationSec);
}

const NO_FILLERS: FillerDetection = { fillers: [] };

/** Hesitation sounds, as strict as the silence setting; filler words from the captions help when there are any. */
export function selectFillers(analysis: AudioAnalysis | null | undefined, preset: SilencePresetId, cues: readonly CaptionCue[] = []): FillerDetection {
  if (!analysis) return NO_FILLERS;
  const levels = measureLevels(analysis.envelope, SILENCE_PRESETS[preset].thresholdRatio);
  return detectFillers(analysis.voice, analysis.envelope, levels, FILLER_PRESETS[preset], cues.flatMap((cue) => cue.words));
}

export interface EditSummary {
  /** Kept parts with their speeds, in playback order. */
  readonly keep: PlayRange[];
  readonly outputDuration: number;
  readonly removedDuration: number;
}

/** Moments near private places, and whether the recipe cuts or blurs them. */
export interface PrivateTime {
  readonly ranges: readonly TimeRange[];
  readonly cut: readonly TimeRange[];
  readonly blur: readonly TimeRange[];
}

export const NO_PRIVATE_TIME: PrivateTime = { ranges: [], cut: [], blur: [] };

/**
 * Private-place time of the video the ride data came from. With a `row`, the
 * times are where its clips show them in the edit; otherwise the video's own.
 */
export function selectPrivateTime(telemetry: Telemetry | null, places: readonly PrivatePlace[], recipe: EditRecipe, durationSec: number, row?: SequenceLayout): PrivateTime {
  const own = privateRanges(telemetry, places, recipe.zones, durationSec);
  const ranges = row ? placeRanges(row, PRIMARY_ASSET, own) : own;
  if (ranges.length === 0) return NO_PRIVATE_TIME;
  return recipe.zones.action === 'cut' ? { ranges, cut: ranges, blur: [] } : { ranges, cut: [], blur: ranges };
}

export function selectEdit(durationSec: number, recipe: EditRecipe, removals: Removals = {}): EditSummary {
  const keep = computeKeepRanges(durationSec, recipe, removals);
  const outputDuration = outputLength(keep);
  return { keep, outputDuration, removedDuration: Math.max(0, durationSec - outputDuration) };
}

/** Scanned tracks the user kept hidden, plus every manually drawn area. */
export const selectMaskTracks = (tracks: readonly Track[], privacy: PrivacySettings, manual: readonly Track[] = privacy.manualTracks): Track[] =>
  privacy.enabled ? [...tracks.filter((track) => isTrackHidden(privacy, track.id)), ...manual] : [];

export function selectAudioGain(analysis: AudioAnalysis | null | undefined, audio: AudioSettings): number {
  if (!analysis || !audio.normalize || audio.muted) return 1;
  return normalizationGain(analysis.loudness);
}

/** All zoom moments found in a screen recording, for the chosen strength, kept away from notifications. */
export const selectZoomMoments = (activity: ScreenActivity | null, amount: number, notifications: readonly ZoomExclusion[] = []): ZoomSegment[] =>
  activity ? planZoom(activity, amount, notifications) : [];

/** Zoom moments that apply to the export: zoom on, minus the ones turned off. */
export const selectZoom = (activity: ScreenActivity | null, settings: ZoomSettings, notifications: readonly ZoomExclusion[] = []): ZoomSegment[] =>
  settings.enabled ? activeZoomSegments(selectZoomMoments(activity, settings.amount, notifications), settings) : [];

/** The recording's zoom moments where its clips show them in the edit. */
export function placeZoom(row: SequenceLayout, segments: readonly ZoomSegment[]): ZoomSegment[] {
  return row.clips.flatMap((clip) =>
    clip.assetId !== PRIMARY_ASSET
      ? []
      : segments.flatMap((segment) => {
          const start = Math.max(segment.start, clip.in);
          const end = Math.min(segment.end, clip.out);
          if (end <= start) return [];
          return [{ ...segment, id: `${segment.id}@${clip.id}`, start: clip.start + start - clip.in, end: clip.start + end - clip.in }];
        }),
  );
}

/** The recording's camera layer settings, or null when the video has no camera layer. */
export const selectCamera = (recipe: Pick<EditRecipe, 'camera'>, hasCamera: boolean): CameraSettings | null => (hasCamera ? recipe.camera : null);

/** A split frame fits the whole screen beside the camera, so cropping and framing don't apply. */
export const isSplitFrame = (camera: CameraSettings | null): camera is CameraSettings => camera?.layout === 'split';

/**
 * Camera path for smart reframing, or null when the crop is static: the option is
 * off, the frame isn't cropped, or there are no faces to follow.
 */
export function selectFocusPath(
  media: Pick<MediaInfo, 'width' | 'height' | 'durationSec'>,
  format: FormatSettings,
  tracks: readonly Track[],
  privacy: PrivacySettings,
  camera: CameraSettings | null = null,
): FocusPoint[] | null {
  const cropped = format.fit === 'crop' && !isSplitFrame(camera) && Math.abs(resolveAspect(format.aspect, media) - media.width / media.height) > 0.01;
  const faces = tracks.filter((track) => track.kind === 'face');
  if (!format.followSubject || !cropped || faces.length === 0) return null;
  return computeFocusPath(faces, media.durationSec, format.focus, new Set(privacy.revealedTrackIds));
}

export type AudioSupport = 'none' | 'unsupported' | 'ok';

/** Whether the source has sound, and whether this browser can process it. */
export function selectAudioSupport(media: Pick<MediaInfo, 'audio'> | null): AudioSupport {
  if (!media?.audio) return 'none';
  return media.audio.decodable ? 'ok' : 'unsupported';
}

/** Ride data to draw, or null when the gauge is off (or there is nothing to draw). */
export const selectOverlayTelemetry = (telemetry: Telemetry | null, settings: TelemetrySettings): Telemetry | null =>
  settings.enabled && settings.fields.length > 0 ? telemetry : null;

export const selectLayout = (media: Pick<MediaInfo, 'width' | 'height'>, format: FormatSettings, camera: CameraSettings | null = null): FrameLayout =>
  layoutWithCamera({ width: media.width, height: media.height }, format, camera);

// ——— The edit as a row of clips ———

/** What the selectors need to know about an imported video, picture or sound. */
export interface AssetShape {
  readonly kind: 'video' | 'image' | 'audio';
  readonly durationSec: number;
  readonly width: number;
  readonly height: number;
}

export type AssetLookup = (assetId: AssetId) => AssetShape | undefined;

/** The row of clips; `media` is the opened video, the others come from `assets`. */
export function selectRow(media: Pick<MediaInfo, 'width' | 'height' | 'durationSec'>, recipe: Pick<EditRecipe, 'sequence'>, assets: AssetLookup = () => undefined): SequenceLayout {
  const frame: Size = { width: media.width, height: media.height };
  const lookup = (id: AssetId) => (id === PRIMARY_ASSET ? media : assets(id)?.kind === 'video' ? assets(id) : undefined);
  return layoutOf(recipe, { lookup, frame, primary: media });
}

/** Found tracks and drawn areas of every video, placed on the row. */
export function selectPlacedTracks(row: SequenceLayout, tracksOf: (assetId: AssetId) => readonly Track[], manual: readonly ManualTrack[]) {
  return {
    scanned: placeTracks(row, tracksOf),
    manual: placeTracks(row, (assetId) => manual.filter((track) => (track.assetId ?? PRIMARY_ASSET) === assetId)),
  };
}

/** The moment of the opened video showing at row time `t` (its ride data and camera follow it), or null. */
export function primaryTimeAt(row: SequenceLayout, t: number): number | null {
  const located = assetTimeAt(row, t);
  return located && located.clip.assetId === PRIMARY_ASSET ? located.time : null;
}

/** Where someone speaks in the finished video (output time): what music ducks under. */
export function selectSpeech(analysis: AudioAnalysis | null | undefined, keep: readonly PlayRange[], durationSec: number, muted: boolean): TimeRange[] {
  if (!analysis || muted || !(durationSec > 0)) return [];
  const { silences, levels } = detectSilences(analysis.envelope, SILENCE_PRESETS.balanced, durationSec);
  if (!levels) return [];
  return toOutputRanges(keep, intersectRanges(invertRanges(silences, range(0, durationSec)), keep));
}

/** Layers with something left to show after the edit, ready for the preview and the export. */
export const selectLayers = (recipe: Pick<EditRecipe, 'layers'>, keep: readonly PlayRange[], assets: AssetLookup): ResolvedLayer[] =>
  resolveLayers(
    recipe.layers,
    keep,
    (id) => {
      const asset = assets(id);
      return asset && { durationSec: asset.durationSec, aspect: asset.height > 0 ? asset.width / asset.height : 1 };
    },
    measureTextAspect,
  );

/** Each clip as the exporter plays it, with its own loudness evening-out (videos are recorded at different levels). */
export function selectExportClips(row: SequenceLayout, soundOf: (assetId: AssetId) => AudioAnalysis | null | undefined, audio: AudioSettings): ExportClip[] {
  return row.clips.map((clip) => ({
    id: clip.id,
    assetId: clip.assetId,
    in: clip.in,
    out: clip.out,
    start: clip.start,
    rect: clip.rect,
    gain: selectAudioGain(soundOf(clip.assetId), audio),
  }));
}

export interface ExportInput {
  /** The opened video: the edit's frame, its ride data, camera and screen analysis. */
  readonly media: Pick<MediaInfo, 'width' | 'height' | 'durationSec'> & Partial<Pick<MediaInfo, 'frameRate'>>;
  readonly recipe: EditRecipe;
  /** Sound analysis of each video (the opened one is `PRIMARY_ASSET`). */
  readonly soundOf?: (assetId: AssetId) => AudioAnalysis | null | undefined;
  /** Faces and plates found in each video, in its own time. */
  readonly tracksOf?: (assetId: AssetId) => readonly Track[];
  readonly telemetry?: Telemetry | null;
  readonly places?: readonly PrivatePlace[];
  readonly hasCamera?: boolean;
  readonly screen?: ScreenActivity | null;
  readonly notifications?: readonly ZoomExclusion[];
  /** Every other imported video, picture and sound. */
  readonly assets?: AssetLookup;
}

/** What the edit is made of: its row, its sound and the parts of the row it keeps. */
export interface KeptEdit {
  readonly row: SequenceLayout;
  readonly sound: ReturnType<typeof placeSound>;
  readonly privateTime: PrivateTime;
  readonly keep: PlayRange[];
}

/**
 * The parts of the row the finished video keeps. Shared by the export and by
 * what only needs to look at those parts (finding faces), so both agree.
 */
export function selectKept(input: Pick<ExportInput, 'media' | 'recipe' | 'soundOf' | 'telemetry' | 'places' | 'assets'>): KeptEdit {
  const { media, recipe, soundOf = () => null, telemetry = null, places = [], assets = () => undefined } = input;
  const row = selectRow(media, recipe, assets);
  const sound = placeSound(row, soundOf);
  const { silences } = selectSilences(sound, recipe.silence.preset, row.duration);
  const { fillers } = selectFillers(sound, recipe.silence.preset, spokenCues(recipe.captions));
  const privateTime = selectPrivateTime(telemetry, places, recipe, media.durationSec, row);
  const keep = selectEdit(row.duration, recipe, { silences, fillers, privateCuts: privateTime.cut }).keep;
  return { row, sound, privateTime, keep };
}

/**
 * Everything the exporter needs, derived from the sources, the recipe and the
 * analyses. Shared by the editor and batch processing so both export alike.
 */
export function selectExportJob(input: ExportInput): Omit<ExportJob, 'outputName' | 'camera' | 'cameraBackdrop' | 'sources'> {
  const { recipe, soundOf = () => null, tracksOf = () => [], telemetry = null, hasCamera = false, screen = null, notifications = [], assets = () => undefined } = input;
  const { row, sound, privateTime, keep: keepRanges } = selectKept(input);
  const placed = selectPlacedTracks(row, tracksOf, recipe.privacy.manualTracks);
  const layers = selectLayers(recipe, keepRanges, assets);
  return {
    recipe,
    keepRanges,
    veil: privateTime.blur,
    maskTracks: selectMaskTracks(placed.scanned, recipe.privacy, placed.manual),
    focusPath: selectFocusPath({ ...row.frame, durationSec: row.duration }, recipe.format, placed.scanned, recipe.privacy, selectCamera(recipe, hasCamera)),
    telemetry: selectOverlayTelemetry(telemetry, recipe.telemetry),
    zoom: placeZoom(row, selectZoom(screen, recipe.zoom, notifications)),
    clips: selectExportClips(row, soundOf, recipe.audio),
    frame: row.frame,
    frameRate: input.media.frameRate ?? null,
    layers,
    speech: layers.some((resolved) => hasSound(resolved.layer) && resolved.layer.duck) ? selectSpeech(sound, keepRanges, row.duration, recipe.audio.muted) : [],
  };
}
